'use client'

import { ArrowSquareOutIcon } from '@phosphor-icons/react'
import type { Entry } from '@/document'

/** Map well-known app names to their domains for favicon derivation. */
const APP_DOMAIN_MAP: Record<string, string> = {
  linkedin: 'linkedin.com',
  reddit: 'reddit.com',
  twitter: 'twitter.com',
  'twitter/x': 'x.com',
  x: 'x.com',
  instagram: 'instagram.com',
  facebook: 'facebook.com',
  youtube: 'youtube.com',
  github: 'github.com',
  slack: 'slack.com',
  safari: 'apple.com',
  chrome: 'google.com',
  arc: 'arc.net',
  notion: 'notion.so',
  discord: 'discord.com',
  threads: 'threads.net',
  mastodon: 'mastodon.social',
  bluesky: 'bsky.app',
  'hacker news': 'news.ycombinator.com',
  hackernews: 'news.ycombinator.com',
}

function getFaviconUrl(domain: string): string {
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`
}

function deriveFavicon(sourceApp: string): string | undefined {
  const domain = APP_DOMAIN_MAP[sourceApp.toLowerCase()]
  return domain ? getFaviconUrl(domain) : undefined
}

function extractDomain(url: string): string | undefined {
  try {
    const hostname = new URL(url).hostname
    return hostname.replace(/^www\./, '')
  } catch {
    return undefined
  }
}

interface SourceLinkProps {
  entry: Entry
}

/**
 * Compact source indicator for captured entries.
 * Shows [favicon] AppName as a clickable link (when URL exists) or plain label.
 */
export function SourceLink({ entry }: SourceLinkProps) {
  const meta = entry.metadata as Record<string, unknown>
  const sourceApp = meta?.sourceApp as string | undefined
  const sourceFavicon = meta?.sourceFavicon as string | undefined

  if (!entry.url && !sourceApp) return null

  const urlDomain = entry.url ? extractDomain(entry.url) : undefined
  const favicon =
    sourceFavicon ||
    (sourceApp ? deriveFavicon(sourceApp) : undefined) ||
    (urlDomain ? getFaviconUrl(urlDomain) : undefined)
  const label = sourceApp || urlDomain || 'Source'

  const className =
    'text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-xs transition-colors'

  const content = (
    <>
      {favicon && (
        <img
          src={favicon}
          alt=""
          width={14}
          height={14}
          className="shrink-0 rounded-sm"
          loading="lazy"
        />
      )}
      <span>{label}</span>
    </>
  )

  if (entry.url) {
    return (
      <a
        href={entry.url}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {content}
        <ArrowSquareOutIcon className="h-3 w-3 shrink-0 opacity-60" />
      </a>
    )
  }

  return <span className={className}>{content}</span>
}
